import React from 'react';
import { SHOW_UNIT_COLUMN, formatQuantity, rowKeyOf } from './recipeBookStore';

/**
 * One book row: S.No · INGREDIENT · QTY [· UNIT] · 🗑
 *
 * SPEC §8.1 — the name is plain text. Not a disabled input: "a disabled input invites
 * someone to enable it later." Only the quantity cell is an input, and it holds the
 * NUMBER ALONE — no unit inside it, no unit appended to it.
 *
 * SPEC §8.2 — there is no add control here, and there is none anywhere in this screen.
 *
 * Row states are §12.4: clean · edited · invalid · pending delete · conflicted ·
 * read-only. "Just synced" belongs to realtime, which is not part of this delivery.
 */
export default function RecipeBookRow({
  row,
  serial,
  pendingChange,
  pendingDelete,
  conflict,
  onChange,
  onToggleDelete,
  onKeepMine,
  onTakeTheirs,
  deleteWarning,
}) {
  const key = rowKeyOf(row);
  const editable = row.editable !== false && !pendingDelete;
  const value = pendingChange ? pendingChange.raw : formatQuantity(row.quantity ?? row.per_plate);

  const classes = ['rb-row'];
  if (pendingDelete) classes.push('rb-row-deleting');
  else if (conflict) classes.push('rb-row-conflict');
  else if (pendingChange && !pendingChange.valid) classes.push('rb-row-invalid');
  else if (pendingChange) classes.push('rb-row-edited');
  if (row.editable === false) classes.push('rb-row-readonly');

  return (
    <>
      <tr className={classes.join(' ')} data-rb-key={key}>
        <td className="rb-sno">{serial}</td>
        <td className="rb-name">
          {row.display_name || row.name}
          {row.shared_with_item_count > 1 && (
            <span className="rb-shared" title={`Shared with ${row.shared_with_item_count} menu items`}>
              shared ×{row.shared_with_item_count}
            </span>
          )}
        </td>
        <td className="rb-qty">
          {editable ? (
            <input
              type="text"
              inputMode="decimal"
              className="rb-qty-input"
              value={value}
              aria-label={`Quantity for ${row.name}`}
              onChange={(e) => onChange(row, e.target.value)}
            />
          ) : (
            <span className="rb-qty-static" title={row.locked_reason || undefined}>
              {formatQuantity(row.quantity ?? row.per_plate)}
            </span>
          )}
        </td>
        {SHOW_UNIT_COLUMN && <td className="rb-unit">{row.unit_symbol}</td>}
        <td className="rb-actions">
          {row.deletable !== false ? (
            <button
              type="button"
              className={`rb-del${pendingDelete ? ' rb-del-undo' : ''}`}
              onClick={() => onToggleDelete(row, { warning: deleteWarning })}
              title={pendingDelete ? 'Keep this row' : 'Remove this row'}
              aria-label={pendingDelete ? `Keep ${row.name}` : `Remove ${row.name}`}
            >
              {pendingDelete ? '↩' : '🗑'}
            </button>
          ) : (
            <span className="rb-del-blocked" title={row.locked_reason || 'This row cannot be removed here'}>—</span>
          )}
        </td>
      </tr>

      {pendingChange && !pendingChange.valid && (
        <tr className="rb-note-row">
          <td />
          <td colSpan={SHOW_UNIT_COLUMN ? 4 : 3} className="rb-error">{pendingChange.error}</td>
        </tr>
      )}

      {pendingChange && pendingChange.valid && (
        <tr className="rb-note-row">
          <td />
          <td colSpan={SHOW_UNIT_COLUMN ? 4 : 3} className="rb-was">
            was {formatQuantity(pendingChange.oldValue)}
          </td>
        </tr>
      )}

      {pendingDelete && (
        <tr className="rb-note-row">
          <td />
          <td colSpan={SHOW_UNIT_COLUMN ? 4 : 3} className="rb-warn">
            Will be removed when you confirm{deleteWarning ? ` — ${deleteWarning}` : ''}
          </td>
        </tr>
      )}

      {conflict && (
        <tr className="rb-note-row">
          <td />
          <td colSpan={SHOW_UNIT_COLUMN ? 4 : 3} className="rb-conflict">
            Someone changed this to {formatQuantity(conflict.theirValue)}
            <button type="button" className="rb-mini" onClick={() => onKeepMine(key, conflict.theirVersion)}>
              Keep mine
            </button>
            <button type="button" className="rb-mini" onClick={() => onTakeTheirs(key)}>
              Take theirs
            </button>
          </td>
        </tr>
      )}
    </>
  );
}
